import * as React from 'react';
import { Alert, Button, Input, Modal, Panel, Select } from '@ixicare/ui';
import {
  Beacon,
  Device,
  DeviceEntryProps,
  DeviceType,
  ECoordsSrid,
  Gateway,
  TUpdateCoords,
  deviceTypesInstallable,
  SRD_MAX_ID,
  iBeaconVendors,
  isMacAddress,
  parseDeviceQr,
  srdIdToMacAddress,
} from '@ixicare/resources';
import { t } from '~i18n';
import { ScanResult, ScannableInput } from '~components/DeviceScanner';
import { OnMarkerInsertEvent } from '~components/Map/DynamicMap';
import { useMutation } from '@tanstack/react-query';
import { getFieldError, humaniseErrors } from '~utils/error';
import { Spacer } from '~components/Utils/Spacer';
import { capitalize } from 'lodash';

export const buttonOptions = [
  {
    label: t('device.types.regular_button'),
    value: 'REGULAR_BUTTON',
  },
  {
    label: t('devices.table.emergencyButton'),
    value: 'EMERGENCY_BUTTON',
  },
];

export function CreateInstalledDeviceModal({
  onClose,
  onRefresh,
  newLocation,
  siteId,
  coordSrid = ECoordsSrid.Indoor,
}) {
  const [code, setCode] = React.useState<string>('');
  // Beacons we don't make carry no ixi code, so they are entered by hand. Some advertise an address
  // we can key on, some are identified only by what they advertise; the source says which, and its
  // unset value means the ordinary scan-a-code path.
  const [beaconSource, setBeaconSource] = React.useState<string>(BEACON_SOURCE_CODE);
  const [beaconMac, setBeaconMac] = React.useState<string>('');
  const [srdId, setSrdId] = React.useState<string>('');
  const [ibeaconMajor, setIbeaconMajor] = React.useState<string>('');
  const [ibeaconMinor, setIbeaconMinor] = React.useState<string>('');
  const [humanisedError, setHumanizedError] = React.useState<{ title: string; description?: string }>();

  const isManualBeacon = beaconSource !== BEACON_SOURCE_CODE;
  const isPlainMacBeacon = beaconSource === BEACON_SOURCE_MAC;
  const isSrdBeacon = beaconSource === BEACON_SOURCE_SRD;
  const ibeaconUuid = isPlainMacBeacon || isSrdBeacon ? '' : beaconSource;
  const isIBeacon = !!ibeaconUuid;

  const scanned = React.useMemo(() => parseDeviceQr(code), [code]);
  const major = parseIdentityPart(ibeaconMajor);
  const minor = parseIdentityPart(ibeaconMinor);
  // A Twig SRD beacon reports a decimal id rather than an address, and is stored under the address
  // the Twig ingestion already writes for it, so both sides join on the same thing.
  const srdBeaconId = parseIdentityPart(srdId, SRD_MAX_ID);

  const macAddress = isPlainMacBeacon
    ? beaconMac.trim().toUpperCase()
    : isSrdBeacon
      ? srdBeaconId !== undefined
        ? srdIdToMacAddress(srdBeaconId)
        : undefined
      : isIBeacon
        ? undefined
        : scanned.macAddress;
  const type = isManualBeacon ? DeviceType.BEACON : scanned.type;

  // A typed code bypasses the scanner, so the address it carries is shape-checked here
  // too — `06 <anything>` would otherwise create a beacon under a junk address.
  const isIdentified = isIBeacon ? major !== undefined && minor !== undefined : isMacAddress(macAddress ?? '');

  const sourceOptions = React.useMemo(
    () => [
      { label: t('deviceDetail.macAddress'), value: BEACON_SOURCE_MAC },
      { label: t('device.form.beaconSourceSrd'), value: BEACON_SOURCE_SRD },
      ...iBeaconVendors.map((vendor) => ({
        label: t('device.form.beaconSourceVendor', { vendor: vendor.name }),
        value: vendor.uuid,
      })),
    ],
    []
  );
  // Only beacons, gateways and wall buttons sit at a fixed spot; the rest are
  // worn or carried and are located via beacons, so a plane coordinate is
  // meaningless for them.
  const isInstallable = !!type && deviceTypesInstallable.includes(type);
  const notInstallable = !!type && !isInstallable;
  // The wrong-type alert already explains an unusable code; don't pile a second
  // message on the same input.
  const macError = !isIdentified && !notInstallable && !!macAddress ? t('device.form.macInvalid') : undefined;

  const typeTranslation = React.useMemo(() => type && t(`device.types.${type.toLowerCase()}`), [type]);
  const [description, setDescription] = React.useState('');
  const [isTouched, setIsTouched] = React.useState(false);

  React.useEffect(() => {
    if (!isTouched) {
      setDescription(
        newLocation?.zoneName ? `${newLocation.zoneName} ${capitalize(typeTranslation?.toLowerCase() ?? '-')}` : ''
      );
    }
  }, [typeTranslation, newLocation]);

  const [emergencyButton, setEmergencyButton] = React.useState<DeviceEntryProps['subType']>('REGULAR_BUTTON');

  const createMutation = useMutation({
    mutationFn: () =>
      createDevice(type!, macAddress ?? '', description, newLocation, siteId, coordSrid, emergencyButton, {
        uuid: ibeaconUuid,
        major,
        minor,
      }),
    onSuccess: () => {
      onRefresh();
      onClose();
    },
    onError: (err) => {
      setHumanizedError(humaniseErrors(err));
    },
  });

  return (
    <Modal style={{ maxWidth: 450 }}>
      <>
        <h3 className="text-center mb-30">{t('device.form.create', { type: typeTranslation })}</h3>
        {createMutation.error && <Alert type="error">{humanisedError?.description ?? humanisedError?.title}</Alert>}
        {isManualBeacon ? (
          <>
            <Panel.Subtitle>{t('device.form.beaconSource')}</Panel.Subtitle>
            <Select
              value={beaconSource}
              onChange={(value) => setBeaconSource(value ?? BEACON_SOURCE_MAC)}
              options={sourceOptions}
            />
            <Spacer size={16} />
            {isPlainMacBeacon ? (
              <Input
                label={t('deviceDetail.macAddress')}
                autoCapitalize="characters"
                value={beaconMac}
                onValueChange={setBeaconMac}
                error={macError ?? getFieldError('macAddress', createMutation.error as Error)}
              />
            ) : isSrdBeacon ? (
              <Input
                label={t('device.form.srdBeaconId')}
                value={srdId}
                onValueChange={setSrdId}
                error={getFieldError('macAddress', createMutation.error as Error)}
              />
            ) : (
              <>
                <Input
                  label={t('device.form.ibeaconMajor')}
                  value={ibeaconMajor}
                  onValueChange={setIbeaconMajor}
                  error={getFieldError('ibeaconMajor', createMutation.error as Error)}
                />
                <Spacer size={16} />
                <Input
                  label={t('device.form.ibeaconMinor')}
                  value={ibeaconMinor}
                  onValueChange={setIbeaconMinor}
                  error={getFieldError('ibeaconMinor', createMutation.error as Error)}
                />
              </>
            )}
            <Button buttonType="link" onClick={() => setBeaconSource(BEACON_SOURCE_CODE)}>
              {t('device.form.beaconUseCode')}
            </Button>
          </>
        ) : (
          <>
            <ScannableInput
              label={t('device.form.code')}
              autoCapitalize="characters"
              value={code}
              onValueChange={setCode}
              // The field holds the whole code, so hand it back verbatim and let
              // the parse above split off the type number.
              onScan={(result) => setCode(result.raw)}
              validateScan={validateInstallable}
              scanHint={t('deviceScan.hintQr')}
              error={
                macError ??
                (getFieldError('deviceType', createMutation.error as Error) ||
                  getFieldError('macAddress', createMutation.error as Error))
              }
            />
            {/* Typed codes bypass the scanner's check, so the same rule is stated here. */}
            {notInstallable && (
              <>
                <Spacer size={16} />
                <Alert type="error">{t('device.form.infobox.notInstallable')}</Alert>
              </>
            )}
            <Button buttonType="link" onClick={() => setBeaconSource(BEACON_SOURCE_MAC)}>
              {t('device.form.beaconAddManually')}
            </Button>
          </>
        )}
        <Spacer size={16} />
        {type == 'WALL_MOUNTED_DISTRESS_BUTTON' && (
          <>
            <Panel.Subtitle>{t('device.form.subtype')}</Panel.Subtitle>

            <Select
              value={t(`${emergencyButton}`)}
              onChange={(value) => {
                setEmergencyButton(value);
              }}
              options={buttonOptions}
            />

            <Spacer size={32} />
          </>
        )}
        <Input
          label={t('device.form.description')}
          value={description}
          onValueChange={(x) => {
            setDescription(x);
            setIsTouched(true);
          }}
          error={getFieldError('description', createMutation.error as Error)}
        />
        <Spacer size={32} />
        <Button
          className="mb-15 error"
          disabled={createMutation.isPending || !description.length || !isInstallable || !isIdentified}
          loading={createMutation.isPending}
          isBlock
          buttonType="success"
          type="button"
          onClick={() => createMutation.mutate()}
        >
          {t('device.form.confirm')}
        </Button>
        <Button onClick={() => onClose()} disabled={createMutation.isPending} isBlock buttonType="link">
          {t('global.cancel')}
        </Button>
      </>
    </Modal>
  );
}

/** Not a vendor UUID: an ixi code carries its own type, and a plain address is always a beacon. */
const BEACON_SOURCE_CODE = '';
const BEACON_SOURCE_MAC = 'mac';
const BEACON_SOURCE_SRD = 'srd';

/** An identity the beacon reports as a plain number, or undefined when it isn't one. Empty is not zero. */
const parseIdentityPart = (value: string, max = 0xffff): number | undefined => {
  if (!value.trim().length) return undefined;
  const parsed = Number(value);
  return Number.isInteger(parsed) && parsed >= 0 && parsed <= max ? parsed : undefined;
};

/** Refuses a scanned code the floor plan can't place, so the camera stays open. */
const validateInstallable = (result: ScanResult) =>
  result.type && deviceTypesInstallable.includes(result.type) ? undefined : t('device.form.infobox.notInstallable');

const createDevice = async (
  type: DeviceType,
  macAddress: string,
  description: string,
  location: OnMarkerInsertEvent,
  siteId: number,
  coordSrid: ECoordsSrid = ECoordsSrid.Indoor,
  emergencyButton?: string | null,
  ibeacon?: { uuid: string; major?: number; minor?: number }
) => {
  // Cast: TUpdateCoords.srid is typed as the Indoor literal in @ixicare/resources; geo planes
  // need Outdoor (4326). Runtime value is correct
  // TODO: drop the cast once the resources type is relaxed to ECoordsSrid.
  const installationCoords = {
    x: location.x,
    y: location.y,
    srid: coordSrid,
  } as TUpdateCoords;

  if (type === DeviceType.BEACON) {
    // Without an address the backend gives the beacon the identity devices report for it.
    return Beacon.submitBeacon({
      macAddress: ibeacon?.uuid ? undefined : macAddress,
      ibeaconUuid: ibeacon?.uuid || undefined,
      ibeaconMajor: ibeacon?.uuid ? ibeacon.major : undefined,
      ibeaconMinor: ibeacon?.uuid ? ibeacon.minor : undefined,
      distanceToLeftWall: 0,
      distanceToRightWall: 0,
      description,
      zoneId: location.zoneUuid,
      planeId: location.planeId,
      installationCoords,
    });
  }
  if (type === DeviceType.GATEWAY) {
    return Gateway.submitGateway({
      macAddress,
      description,
      zoneId: location.zoneUuid!,
      installationCoords,
      planeId: location.planeId,
    });
  }
  if (emergencyButton == 'EMERGENCY_BUTTON') {
    return Device.submitDevice({
      siteId: siteId,
      deviceType: type,
      macAddress,
      description,
      planeId: location.planeId,
      installationCoords,
      subType: emergencyButton,
    });
  } else {
    return Device.submitDevice({
      siteId: siteId,
      deviceType: type,
      macAddress,
      description,
      planeId: location.planeId,
      installationCoords,
    });
  }
};
